import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { AuthService } from '../../services/auth.service';
import type { MitConfigDTO, UpdateMitConfigRequest } from '../../core/models';
import { MitService } from '../../services/mit.service';

/**
 * Configuración MIT de la compañía de la sesión: lectura con `MIT_MANDATE_READ`, edición solo con `MIT_CONFIG_WRITE`.
 * Se guarda con el botón general de la página (ver {@link buildRequest}).
 */
@Component({
  selector: 'app-company-mit-config',
  templateUrl: './company-mit-config.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FloatLabelModule,
    InputNumberModule,
    InputTextModule,
    ToggleSwitchModule
  ]
})
export class CompanyMitConfigComponent {
  private fb = inject(FormBuilder);
  private auth = inject(AuthService);
  private mitService = inject(MitService);
  private companyId = this.auth.getCompanyId();

  readonly canEdit = this.auth.hasRole('MIT_CONFIG_WRITE');
  config = signal<MitConfigDTO | null>(null);
  loading = signal(true);
  loadError = signal<string | null>(null);

  form = this.fb.group({
    enabled: [false],
    maxAmountPerTransactionCap: this.fb.control<number | null>(null, Validators.min(0.01)),
    maxAmountPerMonthPerMandateCap: this.fb.control<number | null>(null, Validators.min(0.01)),
    maxAmountPerDayCap: this.fb.control<number | null>(null, Validators.min(0.01)),
    maxTransactionsPerMinute: this.fb.control<number | null>(null, Validators.min(1)),
    maxMandateValidityDays: this.fb.control<number | null>(null, Validators.min(1)),
    verificationAmount: this.fb.control<number | null>(null, Validators.min(0.01)),
    enrollmentLinkTtlHours: this.fb.control<number | null>(null, Validators.min(1)),
    allowedCardTypes: ['']
  });

  constructor() {
    if (!this.canEdit) this.form.disable();
    this.load();
  }

  /** Inválida solo si el usuario puede editarla; bloquea el guardado general de la página. */
  isInvalid(): boolean {
    return this.canEdit && this.form.invalid;
  }

  /** Sección para el guardado general de la página; nulo si el usuario no puede editarla o no cargó. */
  buildRequest(): UpdateMitConfigRequest | null {
    if (!this.canEdit || this.loading() || this.loadError()) return null;
    const raw = this.form.getRawValue();
    return {
      enabled: raw.enabled === true,
      maxAmountPerTransactionCap: raw.maxAmountPerTransactionCap,
      maxAmountPerMonthPerMandateCap: raw.maxAmountPerMonthPerMandateCap,
      maxAmountPerDayCap: raw.maxAmountPerDayCap,
      maxTransactionsPerMinute: raw.maxTransactionsPerMinute,
      maxMandateValidityDays: raw.maxMandateValidityDays,
      verificationAmount: raw.verificationAmount,
      enrollmentLinkTtlHours: raw.enrollmentLinkTtlHours,
      allowedCardTypes: (raw.allowedCardTypes ?? '').split(',').map((t) => t.trim().toUpperCase()).filter(Boolean)
    };
  }

  private load(): void {
    if (this.companyId == null) {
      this.loading.set(false);
      this.loadError.set('No se pudo identificar la compañía de la sesión.');
      return;
    }
    this.mitService.getConfig(this.companyId).subscribe({
      next: (response) => {
        this.loading.set(false);
        this.apply(response.body ?? null);
      },
      error: () => {
        this.loading.set(false);
        this.loadError.set('No se pudo cargar la configuración de cobros bajo demanda.');
      }
    });
  }

  private apply(config: MitConfigDTO | null): void {
    this.config.set(config);
    this.form.patchValue({
      enabled: config?.enabled === true,
      maxAmountPerTransactionCap: config?.maxAmountPerTransactionCap ?? null,
      maxAmountPerMonthPerMandateCap: config?.maxAmountPerMonthPerMandateCap ?? null,
      maxAmountPerDayCap: config?.maxAmountPerDayCap ?? null,
      maxTransactionsPerMinute: config?.maxTransactionsPerMinute ?? null,
      maxMandateValidityDays: config?.maxMandateValidityDays ?? null,
      verificationAmount: config?.verificationAmount ?? null,
      enrollmentLinkTtlHours: config?.enrollmentLinkTtlHours ?? null,
      allowedCardTypes: (config?.allowedCardTypes ?? []).join(', ')
    });
  }
}
